import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import styles from '../../search.module.scss'
import { FilterDropdown } from '@/components/search/FilterDropdown'
import { Button, ButtonHierarchy } from '@/components/styleguide/Button'
import {
  selectActiveManufacturers,
  selectManufacturersSearchFilter,
} from '@/store/selectors/search'
import { Size } from '@/types/system'

type ManufacturersProps = {
  toggleFilter: () => void
  isFilterOpen: boolean
  onChange: (params: { type: 'multi' | 'single'; filter: string; value: string | number }) => void
}

const GridButton = dynamic(() => import('@/components/search/inputs/GridButton'), { ssr: false })

export const Manufacturers = ({ isFilterOpen, toggleFilter, onChange }: ManufacturersProps) => {
  const current: {
    id: number
    name: string
    vehicleTypes: number[]
  }[] = useSelector(selectManufacturersSearchFilter)
  const activeManufacturers: number[] = useSelector(selectActiveManufacturers)
  const [hasSelections, setHasSelections] = useState<boolean>(false)
  const [visibleManufacturers, setVisibleManufacturers] = useState<number>(12)

  useEffect(() => {
    if (activeManufacturers.length > 0) {
      setHasSelections(true)
    } else {
      setHasSelections(false)
    }
  }, [activeManufacturers])

  const onShowMore = () => {
    if (visibleManufacturers === 12) {
      setVisibleManufacturers(current.length)
    } else {
      setVisibleManufacturers(12)
    }
  }

  return (
    <FilterDropdown
      title="Make"
      toggleFilter={toggleFilter}
      isFilterOpen={isFilterOpen}
      hasSelectedOptions={hasSelections}
      maximumNumberOfVisibleSelectedOptions={3}
      selectedOptions={
        hasSelections
          ? activeManufacturers
              .map(i => current.filter(j => j.id === i)[0]?.name)
              .sort((a, b) => a.localeCompare(b))
          : ['All']
      }>
      <>
        {current.slice(0, visibleManufacturers).length > 0 ? (
          <>
            <div className={styles.grid}>
              {current.slice(0, visibleManufacturers).map(item => (
                <GridButton
                  key={item.id}
                  item={item}
                  onChange={onChange}
                />
              ))}
            </div>
            {current.length > 12 && (
              <Button
                onClick={onShowMore}
                className={styles['show-more']}
                label={`Show ${visibleManufacturers === 12 && current.length > 12 ? 'more' : 'less'}`}
                iconPosition="right"
                icon={
                  visibleManufacturers === 12 && current.length > 12 ? 'ChevronDown' : 'ChevronUp'
                }
                size={Size.Large}
                hasPadding={false}
                hasUnderline
                hasBackground={false}
                hierarchy={ButtonHierarchy.BorderlessGray}
              />
            )}
          </>
        ) : (
          <p className={styles['error-text']}>
            {'There are no "Make" options available based on your current filter selections'}
          </p>
        )}
      </>
    </FilterDropdown>
  )
}
